// Notifications page
import React, { useEffect, useState } from 'react';
import { EmptyState } from '../components/EmptyState';

type Notif = {
  id: string;
  type: 'resonate' | 'reply';
  actor_username: string | null;
  actor_display_name: string | null;
  actor_avatar_url: string | null;
  post_track_name: string | null;
  post_caption: string | null;
  read: number;
  created_at: number;
};

interface Props {
  onNavigate: (page: string, params?: Record<string, string>) => void;
}

export function NotificationsPage({ onNavigate }: Props) {
  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch('/api/v1/notifications');
        if (!res.ok) return;
        const json = await res.json() as { data: Notif[] };
        if (mounted) setNotifs(json.data ?? []);
      } finally {
        if (mounted) setLoading(false);
      }
      // Mark all read after fetching
      fetch('/api/v1/notifications/read', { method: 'POST' }).catch(() => {});
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <h2 style={{ fontSize: 17, fontWeight: 600 }}>Notifications</h2>
      </header>

      {loading && <div style={mutedStyle}>Loading...</div>}

      {!loading && notifs.length === 0 && (
        <EmptyState heading="No notifications yet" body="When someone resonates or replies to your moments, you'll see it here." />
      )}

      <div style={listStyle}>
        {notifs.map(n => (
          <NotifRow key={n.id} notif={n} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}

function NotifRow({ notif, onNavigate }: { notif: Notif; onNavigate: (page: string, params?: Record<string, string>) => void }) {
  const actor = notif.actor_display_name ?? notif.actor_username ?? 'Someone';
  const context = notif.post_track_name
    ? `"${notif.post_track_name}"`
    : notif.post_caption
    ? `"${notif.post_caption.slice(0, 40)}${notif.post_caption.length > 40 ? '…' : ''}"`
    : 'your post';

  const label = notif.type === 'resonate'
    ? `resonated with ${context}`
    : `replied to ${context}`;

  return (
    <div
      onClick={() => notif.actor_username && onNavigate('user', { username: notif.actor_username })}
      style={{ ...rowStyle, background: notif.read === 0 ? 'var(--surface)' : 'transparent', cursor: notif.actor_username ? 'pointer' : 'default' }}
    >
      <NotifAvatar url={notif.actor_avatar_url} name={actor} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{actor}</span>
        {' '}
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{label}</span>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
          {formatRelative(notif.created_at)}
        </div>
      </div>
      <NotifIcon type={notif.type} />
    </div>
  );
}

function NotifAvatar({ url, name }: { url: string | null; name: string }) {
  const [failed, setFailed] = React.useState(false);
  const initial = name[0]?.toUpperCase() ?? '?';
  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        style={avatarStyle}
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div style={{ ...avatarStyle, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

function NotifIcon({ type }: { type: 'resonate' | 'reply' }) {
  if (type === 'resonate') {
    return (
      <svg width={16} height={16} viewBox="0 0 24 24" fill="var(--accent)" stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    );
  }
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 010 8h-1" />
    </svg>
  );
}

function formatRelative(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

const pageStyle: React.CSSProperties = { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' };

const headerStyle: React.CSSProperties = {
  padding: '16px 16px 12px',
  borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10,
};

const listStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column' };

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12,
  padding: '14px 16px',
  borderBottom: '1px solid var(--border)',
};

const mutedStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 14, padding: '16px' };

const avatarStyle: React.CSSProperties = {
  width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0,
};
